import React, { useState, useEffect } from 'react';
import '../../styles/AutomationCard.css';
import { Button, IconButton, Input } from '../';
import { Exit } from '../../assets/icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Edit02Icon } from '@hugeicons/core-free-icons';
import { colour, formatterLong } from '../../styles/GlobalStyles';
import { setAutomation } from '../../functions';

// Helper function to get rules array from value
const getRulesFromValue = (value) => {
    if (value === null || value === undefined) {
        return [];
    }
    if (Array.isArray(value)) {
        return value;
    }
    return [];
};

// Helper function to format number for display
const formatNumberForDisplay = (value) => {
    if (!value || value === '' || value === null || value === undefined) {
        return '';
    }
    if (value === Infinity || (typeof value === 'string' && value.trim().toLowerCase() === 'infinity')) {
        return '';
    }
    const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    if (isNaN(num) || !isFinite(num)) {
        return '';
    }
    return formatterLong.format(num).replace('.00', '').replace('$', '');
};

// Helper function to parse formatted string back to number
const parseFormattedNumber = (value) => {
    if (!value || value === '' || value === null || value === undefined) {
        return '';
    }
    const str = value.toString().trim();
    if (str.toLowerCase() === 'infinity') {
        return 'Infinity';
    }
    const cleaned = str.replace(/[^0-9.-]/g, '');
    return cleaned;
};

// Helper function to create a new rule with default values
const createDefaultRule = () => ({
    roi: '',
    annualizedRoi: '',
    priceToARVRatio: '',
    percentage: null,
    dollarAmount: null
});

// Normalize form rules for comparison to persisted schema
const normalizeFormRulesForCompare = (rules) => {
    return rules
        .map(rule => {
            const hasRoi = rule.roi != null && rule.roi.toString().trim() !== '';
            const hasAnnualizedRoi = rule.annualizedRoi != null && rule.annualizedRoi.toString().trim() !== '';
            const hasPriceToARV = rule.priceToARVRatio != null && rule.priceToARVRatio.toString().trim() !== '';
            const hasPercentage = rule.percentage != null && rule.percentage.toString().trim() !== '';
            const hasDollarAmount = rule.dollarAmount != null && rule.dollarAmount.toString().trim() !== '';

            const hasAnyThreshold = hasRoi || hasAnnualizedRoi || hasPriceToARV || hasPercentage || hasDollarAmount;
            if (!hasAnyThreshold) return null;

            const normalized = {
                minPrice: 0,
                maxPrice: null,
                roi: null,
                annualizedRoi: null,
                maxPurchasePricePercentOfARV: null,
                percentage: null,
                dollarAmount: null
            };

            if (hasRoi) {
                const roiVal = parseFloat(rule.roi);
                if (!isNaN(roiVal) && roiVal >= 0) normalized.roi = roiVal / 100;
            }
            if (hasAnnualizedRoi) {
                const annualizedVal = parseFloat(rule.annualizedRoi);
                if (!isNaN(annualizedVal) && annualizedVal >= 0) normalized.annualizedRoi = annualizedVal / 100;
            }
            if (hasPriceToARV) {
                const ratioVal = parseFloat(rule.priceToARVRatio);
                if (!isNaN(ratioVal) && ratioVal >= 0 && ratioVal <= 100) {
                    normalized.maxPurchasePricePercentOfARV = ratioVal / 100;
                }
            }
            if (hasPercentage) {
                const percentageValue = parseFloat(rule.percentage);
                if (!isNaN(percentageValue) && percentageValue >= 0 && percentageValue <= 100) {
                    normalized.percentage = percentageValue / 100;
                }
            }
            if (hasDollarAmount) {
                const parsed = parseFormattedNumber(rule.dollarAmount);
                const dollarAmount = parseFloat(parsed);
                if (!isNaN(dollarAmount) && dollarAmount >= 0) {
                    normalized.dollarAmount = dollarAmount;
                }
            }
            return normalized;
        })
        .filter(Boolean);
};

const normalizeStoredRulesForCompare = (rules) => {
    return (rules || []).map(rule => ({
        minPrice: rule.minPrice ?? 0,
        maxPrice: rule.maxPrice === Infinity ? null : (rule.maxPrice ?? null),
        roi: rule.roi ?? null,
        annualizedRoi: rule.annualizedRoi ?? null,
        maxPurchasePricePercentOfARV: rule.maxPurchasePricePercentOfARV ?? null,
        percentage: rule.percentage ?? null,
        dollarAmount: rule.dollarAmount ?? null
    }));
};

const formatRuleSummary = (rule) => {
    const parts = [];
    if (rule.dollarAmount !== null && rule.dollarAmount !== undefined) {
        parts.push(`Minimum profit $${formatterLong.format(rule.dollarAmount).replace('$', '').replace('.00', '')}`);
    }
    if (rule.percentage !== null && rule.percentage !== undefined) {
        parts.push(`Minimum profit ${Math.round(rule.percentage * 100)}%`);
    }
    if (rule.roi != null && rule.roi !== undefined && !isNaN(rule.roi)) {
        parts.push(`${Math.round(rule.roi * 100)}% ROI`);
    }
    if (rule.annualizedRoi != null && rule.annualizedRoi !== undefined && !isNaN(rule.annualizedRoi)) {
        parts.push(`${Math.round(rule.annualizedRoi * 100)}% annualized ROI`);
    }
    if (rule.maxPurchasePricePercentOfARV != null && rule.maxPurchasePricePercentOfARV !== undefined && !isNaN(rule.maxPurchasePricePercentOfARV)) {
        parts.push(`Price to ARV ≤ ${Math.round(rule.maxPurchasePricePercentOfARV * 100)}%`);
    }
    return parts.join(' • ');
};

function ProfitRulesSection(props) {
    const { automation, onUpdate, companyId } = props;

    const [editMode, setEditMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formProfitRules, setFormProfitRules] = useState([]);
    const [percentageErrors, setPercentageErrors] = useState({});
    const [hasChanges, setHasChanges] = useState(false);

    // Formula tooltips (from profitCalculation.js)
    const INFO_ROI = 'Profit ÷ Cash Invested × 100. Cash invested = down payment + title + holding costs (loan, taxes, insurance, HOA).';
    const INFO_ANNUALIZED_ROI = 'ROI × (12 ÷ hold months). Normalizes return to a 1-year basis.';
    const INFO_PRICE_TO_ARV = 'Purchase price ÷ ARV × 100. Deal must be at or below this % of ARV.';
    const INFO_PROFIT_PERCENTAGE = 'Profit ÷ ARV × 100. Profit = ARV − rehab − purchase − holding − commission − title − monthly expenses. Holding includes interest on purchase + rehab. Commission is on ARV (sale price).';
    const INFO_PROFIT_DOLLAR = 'Profit in dollars: ARV − rehab − purchase − holding − commission − title − monthly expenses. Monthly expenses = (taxes + insurance + HOA) × hold months.';

    // Initialize form state
    useEffect(() => {
        if (editMode && automation) {
            const profitValue = automation.profitRules?.rules;
            const rules = getRulesFromValue(profitValue);
            const formRules = rules.map(rule => ({
                roi: rule.roi != null && rule.roi !== undefined && !isNaN(rule.roi) ? (rule.roi * 100).toString() : '',
                annualizedRoi: rule.annualizedRoi != null && rule.annualizedRoi !== undefined && !isNaN(rule.annualizedRoi) ? (rule.annualizedRoi * 100).toString() : '',
                priceToARVRatio: rule.maxPurchasePricePercentOfARV != null && rule.maxPurchasePricePercentOfARV !== undefined && !isNaN(rule.maxPurchasePricePercentOfARV) ? (rule.maxPurchasePricePercentOfARV * 100).toString() : '',
                percentage: rule.percentage !== null && rule.percentage !== undefined ? (rule.percentage * 100).toString() : '',
                dollarAmount: rule.dollarAmount !== null && rule.dollarAmount !== undefined ? formatNumberForDisplay(rule.dollarAmount) : ''
            }));
            setFormProfitRules(formRules.length > 0 ? formRules : [createDefaultRule()]);
            setHasChanges(false);
            setPercentageErrors({});
        }
    }, [editMode, automation]);

    // Check for changes
    useEffect(() => {
        if (editMode && automation) {
            const originalProfitValue = automation.profitRules?.rules;
            const originalRules = getRulesFromValue(originalProfitValue);

            const normalizedFormRules = normalizeFormRulesForCompare(formProfitRules);
            const normalizedOriginalRules = normalizeStoredRulesForCompare(originalRules);

            const rulesChanged = JSON.stringify(normalizedFormRules) !== JSON.stringify(normalizedOriginalRules);
            const hasUnsavedDefaults = !originalRules || originalRules.length === 0;
            setHasChanges(rulesChanged || hasUnsavedDefaults);
        }
    }, [formProfitRules, editMode, automation]);

    const updateProfitRule = (index, field, value) => {
        const newRules = [...formProfitRules];
        if (newRules[index]) {
            if (field === 'roi' || field === 'annualizedRoi' || field === 'priceToARVRatio') {
                newRules[index] = { ...newRules[index], [field]: value };
            } else if (field === 'dollarAmount') {
                const parsed = parseFormattedNumber(value);
                newRules[index] = { ...newRules[index], [field]: parsed };
            } else if (field === 'percentage') {
                const numValue = parseFloat(value);
                const newErrors = { ...percentageErrors };
                
                if (value === '' || value === null || value === undefined) {
                    delete newErrors[index];
                } else if (isNaN(numValue)) {
                    newErrors[index] = 'Please enter a valid number';
                } else if (numValue < 0) {
                    newErrors[index] = 'Percentage cannot be negative';
                } else if (numValue > 100) {
                    newErrors[index] = 'Percentage cannot exceed 100%';
                } else {
                    delete newErrors[index];
                }
                
                setPercentageErrors(newErrors);
                newRules[index] = { ...newRules[index], [field]: value };
            } else {
                newRules[index] = { ...newRules[index], [field]: value };
            }
            setFormProfitRules(newRules);
        }
    };

    const handleSave = async () => {
        if (!companyId || !hasChanges) return;
        
        if (Object.keys(percentageErrors).length > 0) {
            alert('Please fix percentage validation errors before saving.');
            return;
        }
        
        setSaving(true);
        try {
            const profitRules = formProfitRules.map(rule => {
                const hasRoi = rule.roi != null && rule.roi.toString().trim() !== '';
                const hasAnnualizedRoi = rule.annualizedRoi != null && rule.annualizedRoi.toString().trim() !== '';
                const hasPriceToARV = rule.priceToARVRatio != null && rule.priceToARVRatio.toString().trim() !== '';
                const hasPercentage = rule.percentage && rule.percentage.trim() !== '';
                const hasDollarAmount = rule.dollarAmount && rule.dollarAmount.trim() !== '';
                
                const hasAnyThreshold = hasRoi || hasAnnualizedRoi || hasPriceToARV || hasPercentage || hasDollarAmount;
                if (!hasAnyThreshold) return null;
                
                const convertedRule = {
                    minPrice: 0,
                    maxPrice: Infinity,
                    roi: null,
                    annualizedRoi: null,
                    maxPurchasePricePercentOfARV: null,
                    percentage: null,
                    dollarAmount: null
                };
                
                if (hasRoi) {
                    const roiVal = parseFloat(rule.roi);
                    if (!isNaN(roiVal) && roiVal >= 0) convertedRule.roi = roiVal / 100;
                }
                if (hasAnnualizedRoi) {
                    const annualizedVal = parseFloat(rule.annualizedRoi);
                    if (!isNaN(annualizedVal) && annualizedVal >= 0) convertedRule.annualizedRoi = annualizedVal / 100;
                }
                if (hasPriceToARV) {
                    const ratioVal = parseFloat(rule.priceToARVRatio);
                    if (!isNaN(ratioVal) && ratioVal >= 0 && ratioVal <= 100) convertedRule.maxPurchasePricePercentOfARV = ratioVal / 100;
                }
                if (hasPercentage) {
                    const percentageValue = parseFloat(rule.percentage);
                    if (!isNaN(percentageValue) && percentageValue >= 0 && percentageValue <= 100) {
                        convertedRule.percentage = percentageValue / 100;
                    }
                }
                if (hasDollarAmount) {
                    const parsed = parseFormattedNumber(rule.dollarAmount);
                    const dollarAmount = parseFloat(parsed);
                    if (!isNaN(dollarAmount) && dollarAmount >= 0) {
                        convertedRule.dollarAmount = dollarAmount;
                    }
                }
                return convertedRule;
            });
            
            const validProfitRules = profitRules.filter(r => r !== null);
            
            const payload = {
                profitRules: {
                    rules: validProfitRules
                }
            };
            
            const result = await setAutomation(companyId, automation.id, payload);
            
            if (result.status === 200) {
                if (onUpdate) {
                    onUpdate({
                        ...automation,
                        profitRules: {
                            rules: validProfitRules
                        }
                    });
                }
                setEditMode(false);
                setHasChanges(false);
            } else {
                alert('Failed to save changes. Please try again.');
            }
        } catch (error) {
            console.error("Error saving profit rules:", error);
            alert("Failed to save changes. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setEditMode(false);
        setHasChanges(false);
        setPercentageErrors({});
    };

    const storedRules = getRulesFromValue(automation?.profitRules?.rules);
    const hasConfiguredRules = storedRules.some(rule => (
        rule?.roi != null ||
        rule?.annualizedRoi != null ||
        rule?.maxPurchasePricePercentOfARV != null ||
        rule?.percentage != null ||
        rule?.dollarAmount != null
    ));

    return (
        <div className={`automation-form-section-card automation-detail-section-card ${editMode ? '' : 'automation-form-section-card-compact'}`}>
            
            <div className="automation-form-section-card-content">
                <div className="automation-form-section-card-text">
                    <span className="body-bold colour-primary">Profit Rules</span>
                    {!hasConfiguredRules && (
                        <span className="body-regular colour-secondary">
                            Choose when properties should be flagged as profitable.
                        </span>
                    )}
                </div>
                <div className="automation-form-section-card-icon-button">
                    <IconButton
                        size="medium"
                        icon={editMode ? <Exit fill={colour.red03} size={14} clickFunc={null} /> : <HugeiconsIcon icon={Edit02Icon} color={colour.gray06} size={16} />}
                        disabled={false}
                        clickFunc={editMode ? handleCancel : () => setEditMode(true)}
                        standardIcon={false}
                        background={true}
                        bgType={editMode ? "destructive" : "white"}
                    />
                </div>
            </div>
            
            {editMode ? (
                <div className="automation-underwriting-edit-panel">
                            {formProfitRules.map((rule, index) => {
                                return (
                                    <div key={index} className="automation-profit-rules-rule-item">
                                        
                                        <div className="automation-form-rule-grid automation-form-rule-grid-3">
                                            <Input
                                                label="ROI"
                                                labelType="default"
                                                info={true}
                                                infoText={INFO_ROI}
                                                value={rule.roi ?? ''}
                                                onChange={(e) => updateProfitRule(index, 'roi', e.target.value)}
                                                placeholder="15"
                                                type="text"
                                                rightUnit="%"
                                                unitSwitch={false}
                                                leftUnitSelected={true}
                                                error={false}
                                                onMouseEnter={() => {}}
                                                onMouseLeave={() => {}}
                                            />
                                            <Input
                                                label="Annualized ROI"
                                                labelType="default"
                                                info={true}
                                                infoText={INFO_ANNUALIZED_ROI}
                                                value={rule.annualizedRoi ?? ''}
                                                onChange={(e) => updateProfitRule(index, 'annualizedRoi', e.target.value)}
                                                placeholder="20"
                                                type="text"
                                                rightUnit="%"
                                                unitSwitch={false}
                                                leftUnitSelected={true}
                                                error={false}
                                                onMouseEnter={() => {}}
                                                onMouseLeave={() => {}}
                                            />
                                            <Input
                                                label="Max % of ARV"
                                                labelType="default"
                                                info={true}
                                                infoText={INFO_PRICE_TO_ARV}
                                                value={rule.priceToARVRatio ?? ''}
                                                onChange={(e) => updateProfitRule(index, 'priceToARVRatio', e.target.value)}
                                                placeholder="80"
                                                type="text"
                                                rightUnit="%"
                                                unitSwitch={false}
                                                leftUnitSelected={true}
                                                error={false}
                                                onMouseEnter={() => {}}
                                                onMouseLeave={() => {}}
                                            />
                                        </div>
                                        
                                        <div className="automation-form-rule-grid">
                                            <Input
                                                label="Profit percentage"
                                                labelType="default"
                                                info={true}
                                                infoText={INFO_PROFIT_PERCENTAGE}
                                                value={rule.percentage || ''}
                                                onChange={(e) => updateProfitRule(index, 'percentage', e.target.value)}
                                                placeholder="15"
                                                type="text"
                                                rightUnit="%"
                                                unitSwitch={false}
                                                leftUnitSelected={true}
                                                error={percentageErrors[index] ? true : false}
                                                prompt={percentageErrors[index] || false}
                                                onMouseEnter={() => {}}
                                                onMouseLeave={() => {}}
                                            />
                                            <Input
                                                label="Profit dollar amount"
                                                labelType="default"
                                                info={true}
                                                infoText={INFO_PROFIT_DOLLAR}
                                                value={rule.dollarAmount}
                                                onChange={(e) => updateProfitRule(index, 'dollarAmount', e.target.value)}
                                                placeholder="50,000"
                                                type="text"
                                                leftUnit="$"
                                                leftUnitSelected={true}
                                                unitSwitch={false}
                                                error={false}
                                                onMouseEnter={() => {}}
                                                onMouseLeave={() => {}}
                                            />
                                        </div>
                                        
                                    </div>
                                );
                            })}
                            
                            <div className="automation-form-footer">
                                <div className="automation-form-footer-button">
                                    <Button
                                        buttonType="primary-button"
                                        size="large"
                                        text="Save changes"
                                        cta={handleSave}
                                        disabled={!hasChanges || saving}
                                        spinner={saving}
                                    />
                                </div>
                            </div>
                </div>
            ) : (
                <div className="automation-detail-section-summary">
                    {(() => {
                        const profitValue = automation.profitRules?.rules;
                        const rules = getRulesFromValue(profitValue);
                        
                        if (rules.length === 0) {
                            return (
                                <div className="automation-detail-section-summary-content">
                                    <span className="body-regular colour-secondary">No profit threshold rules configured</span>
                                </div>
                            );
                        }
                        
                        return (
                            <div className="automation-detail-section-summary-content automation-detail-section-summary-rules">
                                {rules.map((rule, index) => (
                                    <div key={index} className="automation-detail-output-rule-block">
                                        <div className="automation-detail-output-line">
                                            <span className="body-regular colour-secondary">{formatRuleSummary(rule)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })()}
                </div>
            )}
        </div>
    );
}

export default ProfitRulesSection;
